import * as Sentry from '@sentry/react';

import type { OnboardingStatus } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { PATHWAYS_ONBOARDING_EVENT_NAME } from 'bundles/onboarding-common/constants';
import useGetLearnerObjectPromise from 'bundles/onboarding-common/hooks/useGetLearnerObjectPromise';
import useLearnerObjectUpdateSkillsWanted from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateSkillsWanted';
import useSetOnboardingSeen from 'bundles/onboarding-common/hooks/useSetOnboardingSeen';
import type { LocalStorageOnboardingDataType } from 'bundles/onboarding-common/types/LocalStorage';
import { clearLocalStorageOnboardingData } from 'bundles/onboarding-common/utils/localStorageUtils';

const useSaveLocalStorageOnboardingToLearnerObject = () => {
  const { getLearnerObjectPromise } = useGetLearnerObjectPromise();
  const { updateSkillsWanted } = useLearnerObjectUpdateSkillsWanted();
  const { setOnboardingSeen } = useSetOnboardingSeen(PATHWAYS_ONBOARDING_EVENT_NAME);
  const track = useTracker();

  const trackSubmitOnboarding = (onboardingStatus: OnboardingStatus) => {
    track('submit_onboarding', {
      form: {
        name: PATHWAYS_ONBOARDING_EVENT_NAME,
      },
      onboardingStatus,
    });
  };

  const saveLocalStorageOnboardingToLearnerObject = async (
    localStorageData: LocalStorageOnboardingDataType,
    shouldClearSkills?: boolean
  ) => {
    try {
      const results = await Promise.all(
        Object.values(localStorageData).map((questionValue) => getLearnerObjectPromise({ questionValue }))
      );

      // Runs after the loop so it overrides any skillsWanted update the skillSelect entry above may have made.
      const clearSkillsResult = shouldClearSkills ? await updateSkillsWanted([]) : undefined;

      const hasError = [...results, clearSkillsResult].some((result) => result?.success === false);
      if (hasError) {
        throw new Error('Failed to save one or more LearnerObject fields');
      }

      trackSubmitOnboarding('success');
      clearLocalStorageOnboardingData();
    } catch (error) {
      Sentry.captureMessage('[onboarding] Error saving localStorage onboarding data to learner object', {
        extra: { error },
      });
      trackSubmitOnboarding('user_profile_api_error');
    }

    // Always try to set onboarding seen, even if saving local storage fails
    await setOnboardingSeen(() => trackSubmitOnboarding('onboarding_api_error'));
  };

  return { saveLocalStorageOnboardingToLearnerObject };
};

export default useSaveLocalStorageOnboardingToLearnerObject;
